/**
 * ViewChemicalDialog
 * Read-only view of all chemical configuration details.
 * Matches ChemicalsManagement.jsx style: 4-panel 2×2 card grid.
 */

import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Typography, Grid, Chip, Button, IconButton, Stack, Card, CardContent,
  alpha, useTheme
} from '@mui/material';
import {
  Close as CloseIcon, Edit as EditIcon, Science as ScienceIcon
} from '@mui/icons-material';

function getDensityInfo(item) {
  const qty = parseFloat(item?.primary_unit_quantity);
  const density = parseFloat(item?.density_g_ml);
  const measure = item?.primary_unit_measure;
  if (!qty || !density || density <= 0) return null;
  let volL = null;
  if (measure === 'kg') volL = qty / density;
  else if (measure === 'g') volL = qty / (density * 1000);
  else if (measure === 'lbs') volL = (qty * 0.453592) / density;
  else if (measure === 'oz') volL = (qty * 0.0283495) / density;
  if (volL === null) return null;
  return `≈ ${volL.toFixed(3)} L per ${item.primary_unit_type || 'unit'} (based on ${density.toFixed(3)} g/mL)`;
}

function FieldRow({ label, value }) {
  if (!value) return null;
  return (
    <Box>
      <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
        {label}
      </Typography>
      <Typography variant="body1" sx={{ fontWeight: 500 }}>
        {value}
      </Typography>
    </Box>
  );
}

export default function ViewChemicalDialog({ open, onClose, item, onEdit }) {
  const theme = useTheme();

  if (!item) return null;
  const m = item;

  const cardSx = {
    height: '100%',
    borderRadius: 3,
    border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
    background: 'white',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    cursor: 'default',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: `0 16px 48px ${alpha(theme.palette.primary.main, 0.15)}`,
      border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
    }
  };

  const densityInfo = getDensityInfo(m);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 4,
          border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
          maxHeight: '90vh',
          boxShadow: '0 24px 48px rgba(0,0,0,0.15)',
          bgcolor: alpha(theme.palette.grey[50], 0.95),
          backdropFilter: 'blur(10px)'
        }
      }}
    >
      <DialogTitle sx={{
        pb: 2,
        pt: 3,
        px: 3,
        fontSize: '1.25rem',
        fontWeight: 500,
        color: 'text.primary',
        borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        bgcolor: 'white'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{
            p: 1.5,
            borderRadius: 2,
            bgcolor: alpha(theme.palette.primary.main, 0.1),
            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
          }}>
            <ScienceIcon sx={{ color: 'primary.main' }} />
          </Box>
          <Box>
            <Typography variant="h6" component="span" sx={{ fontWeight: 500, fontSize: '1.1rem', color: 'text.primary' }}>
              Chemical Details
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500, mt: 0.5 }}>
              {m.name}
            </Typography>
          </Box>
        </Box>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{ color: 'text.secondary', '&:hover': { bgcolor: alpha(theme.palette.grey[400], 0.1), color: 'text.primary' } }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ p: 4 }}>
          <Grid container spacing={4}>

            {/* Basic Information */}
            <Grid item xs={12} md={6}>
              <Card sx={cardSx}>
                <CardContent sx={{ p: 4 }}>
                  <Typography variant="h6" sx={{ fontWeight: 500, mb: 3, color: 'primary.main' }}>
                    Basic Information
                  </Typography>
                  <Stack spacing={2}>
                    <FieldRow label="SKU" value={m.sku} />
                    <FieldRow label="Description" value={m.description} />
                    <FieldRow label="Category" value={m.category} />
                    <FieldRow label="Material Type" value={m.material_type} />
                    <FieldRow label="Color" value={m.color} />
                    <FieldRow label="Form" value={m.form} />
                  </Stack>
                </CardContent>
              </Card>
            </Grid>

            {/* Unit Configuration */}
            <Grid item xs={12} md={6}>
              <Card sx={cardSx}>
                <CardContent sx={{ p: 4 }}>
                  <Typography variant="h6" sx={{ fontWeight: 500, mb: 3, color: 'primary.main' }}>
                    Unit Configuration
                  </Typography>
                  <Stack spacing={2}>
                    {m.primary_unit_type && m.primary_unit_quantity && (
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                          Primary Unit
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {m.primary_unit_quantity}{m.primary_unit_measure} per {m.primary_unit_type}
                        </Typography>
                        {densityInfo && (
                          <Typography variant="caption" color="primary.main" sx={{ fontWeight: 500 }}>
                            {densityInfo}
                          </Typography>
                        )}
                      </Box>
                    )}
                    <FieldRow label="Density" value={m.density_g_ml ? `${m.density_g_ml} g/ml` : null} />
                    <FieldRow label="CAS Number" value={m.cas_number} />
                  </Stack>
                </CardContent>
              </Card>
            </Grid>

            {/* Business Information */}
            <Grid item xs={12} md={6}>
              <Card sx={cardSx}>
                <CardContent sx={{ p: 4 }}>
                  <Typography variant="h6" sx={{ fontWeight: 500, mb: 3, color: 'primary.main' }}>
                    Business Information
                  </Typography>
                  <Stack spacing={2}>
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                        Vendor
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {m.supplier?.name || 'No supplier assigned'}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                        Unit Cost
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        ${parseFloat(m.unit_cost || 0).toFixed(2)} per {m.primary_unit_measure || 'unit'}
                      </Typography>
                    </Box>
                    {m.moq && (
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                          Minimum Order Quantity
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {m.moq} {m.primary_unit_type}
                        </Typography>
                      </Box>
                    )}
                    {m.lead_time_days && (
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                          Lead Time
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {m.lead_time_days} days
                        </Typography>
                      </Box>
                    )}
                  </Stack>
                </CardContent>
              </Card>
            </Grid>

            {/* Additional Information */}
            <Grid item xs={12} md={6}>
              <Card sx={cardSx}>
                <CardContent sx={{ p: 4 }}>
                  <Typography variant="h6" sx={{ fontWeight: 500, mb: 3, color: 'primary.main' }}>
                    Additional Information
                  </Typography>
                  <Stack spacing={2}>
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                        Created
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {m.created_at ? new Date(m.created_at).toLocaleDateString() : 'N/A'}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                        Last Updated
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {m.updated_at ? new Date(m.updated_at).toLocaleDateString() : 'N/A'}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary' }}>
                        Status
                      </Typography>
                      <Chip label="Active" color="success" size="small" />
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>

            {/* Notes (full width) */}
            {m.notes && (
              <Grid item xs={12}>
                <Card sx={cardSx}>
                  <CardContent sx={{ p: 4 }}>
                    <Typography variant="h6" sx={{ fontWeight: 500, mb: 2, color: 'text.primary' }}>
                      Notes
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500, lineHeight: 1.6 }}>
                      {m.notes}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            )}

            {/* Storage Requirements (full width) */}
            {(m.storage_requirements || m.storage_temp_min || m.storage_temp_max) && (
              <Grid item xs={12}>
                <Card sx={cardSx}>
                  <CardContent sx={{ p: 4 }}>
                    <Typography variant="h6" sx={{ fontWeight: 500, mb: 3, color: 'primary.main' }}>
                      Storage Requirements
                    </Typography>
                    <Stack spacing={2}>
                      {m.storage_temp_min && (
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                            Storage Temperature Minimum
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>{m.storage_temp_min}°C</Typography>
                        </Box>
                      )}
                      {m.storage_temp_max && (
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                            Storage Temperature Maximum
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>{m.storage_temp_max}°C</Typography>
                        </Box>
                      )}
                      {m.storage_requirements && (
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                            Additional Requirements
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500, lineHeight: 1.6 }}>{m.storage_requirements}</Typography>
                        </Box>
                      )}
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            )}

            {/* Safety Information (full width) */}
            {(m.hazard_classification || m.storage_humidity_max) && (
              <Grid item xs={12}>
                <Card sx={cardSx}>
                  <CardContent sx={{ p: 4 }}>
                    <Typography variant="h6" sx={{ fontWeight: 500, mb: 3, color: 'primary.main' }}>
                      Safety Information
                    </Typography>
                    <Stack spacing={2}>
                      {m.hazard_classification && (
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                            Hazard Classification
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {m.hazard_classification.charAt(0).toUpperCase() + m.hazard_classification.slice(1)}
                          </Typography>
                        </Box>
                      )}
                      {m.storage_humidity_max && (
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                            Maximum Storage Humidity
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>{m.storage_humidity_max}%</Typography>
                        </Box>
                      )}
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            )}

          </Grid>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 4, pt: 2, borderTop: `1px solid ${alpha(theme.palette.grey[300], 0.8)}` }}>
        <Button
          onClick={onClose}
          sx={{ borderRadius: 3, px: 3, py: 1.5, fontWeight: 500 }}
        >
          Close
        </Button>
        {onEdit && (
          <Button
            variant="contained"
            startIcon={<EditIcon />}
            onClick={() => onEdit(item)}
            sx={{
              borderRadius: 3, px: 4, py: 1.5, fontWeight: 500, fontSize: '0.9rem',
              boxShadow: `0 6px 18px ${alpha(theme.palette.primary.main, 0.25)}`,
              '&:hover': {
                transform: 'translateY(-1px)',
                boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.35)}`
              }
            }}
          >
            Edit Chemical
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
